import { post } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

const postWorkplaceComparisonRequestSchema = z.object({
  shiftId: z.string().optional(),
  workplaceAId: z.string(),
  workplaceAName: z.string(),
  workplaceBId: z.string(),
  workplaceBName: z.string(),
  selectedWorkplaceId: z.string().nullable(),
  isTie: z.boolean(),
});

const postWorkplaceComparisonBodySchema = z.object({
  data: z.object({
    type: z.literal("workplace-comparison"),
    attributes: z.object({
      isTie: z.boolean(),
      workplaceAName: z.string(),
      workplaceBName: z.string(),
    }),
    relationships: z.object({
      workplaceA: z.object({ data: z.object({ id: z.string(), type: z.literal("workplace") }) }),
      workplaceB: z.object({ data: z.object({ id: z.string(), type: z.literal("workplace") }) }),
      selectedWorkplace: z.object({
        data: z.object({ id: z.string(), type: z.literal("workplace") }).nullable(),
      }),
      shift: z
        .object({
          data: z.object({ id: z.string(), type: z.literal("shift") }).nullable(),
        })
        .optional(),
    }),
  }),
});

// Response is fire-and-forget — only validate the resource identifier
const postWorkplaceComparisonResponseSchema = z.object({
  data: z.object({
    id: z.string(),
    type: z.literal("workplace-comparison"),
  }),
});

export type PostWorkplaceComparisonRequest = z.infer<typeof postWorkplaceComparisonRequestSchema>;
export type PostWorkplaceComparisonResponse = z.infer<typeof postWorkplaceComparisonResponseSchema>;

const POST_WORKPLACE_COMPARISON_PATH = "/workplace-comparisons";

export function usePostWorkplaceComparison(
  options: UseMutationOptions<
    PostWorkplaceComparisonResponse,
    AxiosError,
    PostWorkplaceComparisonRequest
  > = {}
): UseMutationResult<PostWorkplaceComparisonResponse, AxiosError, PostWorkplaceComparisonRequest> {
  return useMutation({
    mutationFn: async (data: PostWorkplaceComparisonRequest) => {
      const body = {
        data: {
          type: "workplace-comparison" as const,
          attributes: {
            isTie: data.isTie,
            workplaceAName: data.workplaceAName,
            workplaceBName: data.workplaceBName,
          },
          relationships: {
            workplaceA: { data: { id: data.workplaceAId, type: "workplace" as const } },
            workplaceB: { data: { id: data.workplaceBId, type: "workplace" as const } },
            selectedWorkplace: {
              data: data.selectedWorkplaceId
                ? { id: data.selectedWorkplaceId, type: "workplace" as const }
                : null,
            },
            ...(data.shiftId
              ? { shift: { data: { id: data.shiftId, type: "shift" as const } } }
              : {}),
          },
        },
      };

      const response = await post({
        url: `${environmentConfig.REACT_APP_SHIFT_REVIEW_API_URL}${POST_WORKPLACE_COMPARISON_PATH}`,
        data: body,
        requestSchema: postWorkplaceComparisonBodySchema,
        responseSchema: postWorkplaceComparisonResponseSchema,
      });
      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.POST_WORKPLACE_COMPARISON_FAILURE,
    },
    ...options,
  });
}
